<template>
    <Head>
        <title>OCP: Withdrawals</title>
    </Head>

    <authenticated-layout :notifications="notifications" >

        <template #header>
            <organization-nav v-if="organizationId" :organization-id="organizationId"/>
        </template>

        <div class="flex flex-col">

            <div class="flex flex-row justify-between items-baseline px-6 py-4">
                <h1 class="text-2xl font-bold"> Выводы</h1>
            </div>

            <div class="flex flex-row justify-between items-baseline gap-4 px-6 py-4">
                <Link :href="route('withdrawals.auto')" class="font-bold text-blue-600 hover:text-blue-800">Авто выводы ></Link>
                <form
                    class="flex flex-row justify-end items-bottom gap-2"
                    @submit.prevent="filter"
                >
                    <div class="w-96">
                        <v-input placeholder="Поиск по ID/адресу/хешу" type="search" v-model="search"/>
                    </div>
                    <v-button>Найти</v-button>
                </form>
            </div>

            <div class="grid grid-cols-9 p-2 gap-6 items-end text-sm font-bold  bg-white">
                <select
                    class="text-center text-sm font-bold border-0 p-0"
                    :class="{ 'text-blue-700': type !== null}"
                    @change="filter"
                    v-model="type"
                >
                    <option :value="null">Тип</option>
                    <option v-for="t in types" :value="t">{{ t.split('_').join(' ')  }}</option>
                </select>
                <select
                    class="text-center text-sm font-bold border-0 p-0"
                    :class="{ 'text-blue-700': status !== null}"
                    @change="filter"
                    v-model="status"
                >
                    <option :value="null">Статус</option>
                    <option v-for="s in statuses" :value="s">{{ s }}</option>
                </select>
                <span class="text-right">Сумма</span>
                <span>Монета (Сеть)</span>
                <span class="col-span-4">Инфо</span>
                <div class="text-center">Дата (UTC)</div>
            </div>
            <div
                v-if="result.hasOwnProperty('withdrawals')"
                v-for="w of result.withdrawals"
                class="grid grid-cols-9 p-2 gap-6 items-center border-t text-sm  bg-white"
            >

                <div class="text-center">{{ w.type.split('_').join(' ') }}</div>

                <div class="flex flex-col gap-1 items-center">
                    <span>{{ w.status }}</span>
                    <Link
                        v-if="w.id && w.status!=='ERROR' && access('withdrawals', 'refund')"
                        class="cursor-pointer text-red-500 hover:text-green-700"
                        :data="{ id: w.id }"
                        method="post"
                        :href="route('withdrawal.set.error')"
                    >Set Error</Link>
                    <span class="text-green-500 cursor-pointer font-bold hover:text-blue-700"
                          v-if="w.id && w.status==='ERROR' && access('withdrawals', 'refund')"
                          @click="open2FAModal(w.id)">Вернуть</span>
                </div>


                <div class="flex flex-col items-end font-mono">
                    <span class="font-bold">{{ w.amount }}</span>
                    <span class="text-xs" v-if="w.amountUsd">({{ w.amountUsd }}$)</span>
                </div>

                <div>{{ w.currency.name }} ({{ w.network.name }})</div>

                <div class="col-span-4 flex flex-col text-xs text-clip overflow-hidden">
                    <div v-if="!organizationId && w.owner" class="flex flex-row gap-2">
                        <label :for="`${w.id}_org`" class="w-28 text-gray-500">Organization</label>
                        <Link :id="`${w.id}_org`"
                            class="text-blue-500 hover:text-blue-700"
                            :href="route('organization.withdrawals', w.owner.id)"
                        >{{ w.owner.name }}</Link>
                    </div>
                    <div class="flex flex-row gap-2">
                        <label :for="`${w.id}_id`" class="w-28 text-gray-500">Withdrawal ID</label>
                        <code :id="`${w.id}_id`">{{ w.id }}</code>
                    </div>
                    <div v-if="w.transactionHash" class="flex flex-row gap-2">
                        <label :for="`${w.id}_th`" class="w-28 text-gray-500">Transaction Hash</label>
                        <code :id="`${w.id}_th`">{{ w.transactionHash }}</code>
                    </div>
                    <div v-if="w.addressFrom" class="flex flex-row gap-2">
                        <label :for="`${w.id}_af`" class="w-28 text-gray-500" >Address From</label>
                        <Link :id="`${w.id}_af`"
                              class="text-blue-500 hover:text-blue-700"
                              :href="route('addresses', { search: w.addressFrom })"
                        >{{ w.addressFrom}}</Link>
                    </div>
                    <div v-if="w.addressTo" class="flex flex-row gap-2">
                        <label :for="`${w.id}_at`" class="w-28 text-gray-500">Address To</label>
                        <code :id="`${w.id}_at`">{{ w.addressTo }}</code>
                    </div>
                    <div v-if="w.reason" class="flex flex-col gap-1 w-full">
                        <label :for="`${w.id}_rn`" class="text-gray-500">Причина</label>
                        <span :id="`${w.id}_rn`" class="font-mono">{{ w.reason }}</span>
                    </div>
                </div>

                <div class="flex flex-col justify-between items-center">
                    <span>{{ moment(w.createdAt).utc().format('L') }}</span>
                    <span>{{ moment(w.createdAt).utc().format('LTS') }}</span>
                </div>

            </div>

            <v-pagination
                class="py-6"
                :count="parseInt(result.total)"
                :page="page"
                :limit="limit"
                :paginate="paginate"
            />
        </div>

        <v-modal2fa
            :showing="show2FA"
            @close="close2FAModal"
            @submit="submit2FAModal"
            v-model:secret="refund.secret"
            :error="refund.errors.secret"
        />

    </authenticated-layout>

</template>

<script>
import {Head, Link, useForm} from '@inertiajs/inertia-vue3';
import AuthenticatedLayout from '@/Layouts/Authenticated';
import OrganizationNav from '@/Components/Navigation/Organization';
import VInput from "@/Components/Input";
import VButton from "@/Components/Button";
import VSelect from "@/Components/Select";
import VModal2fa from "@/Components/2FAModal";
import moment from "moment";
import VPagination from "@/Components/PaginationOffset.vue";
import OrganizationNavigation from "@/Components/Navigation/Organization.vue";
import Label from "@/Components/Label.vue";

export default {
    components: {
        Label,
        OrganizationNavigation,
        VPagination,
        Head, Link,
        AuthenticatedLayout,
        OrganizationNav,
        VInput,
        VButton,
        VSelect,
        VModal2fa,
    },
    props: {
        notifications: Object,
        success: Boolean,
        result: Object,
        page: Number,
        offset: Number,
        limit: Number,
        search: String,
        type: String,
        types: Array,
        status: String,
        statuses: Array,
        organizationId: String | null,
    },
    setup () {
        const refund = useForm({
            'withdrawalId': null,
            'secret': '',
        });
        return { refund };
    },
    data () {
        return {
            show2FA: false,
        }
    },
    methods: {
        moment,
        filter() {
            const { search, type, status, organizationId } = this;

            useForm({ search, type, status }).get(
                organizationId
                    ? this.route('organization.withdrawals', organizationId)
                    : this.route('withdrawals')
            );
        },
        paginate(page) {
            const { search, type, status, organizationId } = this;

            useForm({ page, search, type, status }).get(
                organizationId
                    ? this.route('organization.withdrawals', organizationId)
                    : this.route('withdrawals')
            );
        },
        open2FAModal(withdrawalId) {
            this.show2FA = true;
            this.refund.withdrawalId = withdrawalId;
            this.refund.secret = '';
        },
        submit2FAModal() {
            this.refund.post(this.route('withdrawal.refund'), {
                onSuccess: () => this.close2FAModal(),
            });
        },
        close2FAModal() {
            this.show2FA = false;
            this.refund.withdrawalId = null;
        },
    },
}
</script>
